import React, { useState } from "react";
import { toast } from "sonner";
import API, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DotsSixVertical, Plus, X, CaretUp, CaretDown } from "@phosphor-icons/react";

const isPdf = (d) => (d.original_filename || "").toLowerCase().endsWith(".pdf") || d.content_type === "application/pdf";
const isSignable = (d) => isPdf(d) || (d.original_filename || "").toLowerCase().endsWith(".docx");

// Launches a native signing request from a PDF in the Documents folder.
// The clicked document is pre-included; additional PDFs on the case can be
// bundled into the SAME envelope, dragged into the order they should sign in.
export default function SendForSigningDialog({ caseId, doc, documents = [], onClose, onCreated }) {
  const [title, setTitle] = useState(doc?.original_filename?.replace(/\.(pdf|docx)$/i, "") || "");
  const [mode, setMode] = useState("deed");
  const [busy, setBusy] = useState(false);
  const [splitOutput, setSplitOutput] = useState(false);
  // Ordered list of documents in the bundle (drag to reorder). Starts with the clicked doc.
  const [items, setItems] = useState([doc].filter(Boolean));
  const [dragIndex, setDragIndex] = useState(null);

  const itemIds = items.map((d) => d.id);
  const available = (documents || []).filter((d) => isPdf(d) && !itemIds.includes(d.id));

  const addDoc = (d) => setItems((s) => [...s, d]);
  const removeDoc = (id) => setItems((s) => (s.length > 1 ? s.filter((d) => d.id !== id) : s));

  const onDrop = (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) return setDragIndex(null);
    setItems((s) => {
      const next = [...s];
      const [moved] = next.splice(dragIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setDragIndex(null);
  };

  const move = (index, dir) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    setItems((s) => {
      const next = [...s];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const create = async () => {
    setBusy(true);
    try {
      const document_ids = items.map((d) => d.id);
      const { data } = await API.post(`/signing/envelopes`, {
        case_id: caseId, document_ids, mode, split_output: document_ids.length > 1 ? splitOutput : false,
        title: title || (document_ids.length > 1 ? `Signing bundle (${document_ids.length} documents)` : doc.original_filename),
      });
      onCreated(data);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="send-for-signing-dialog" className="w-[92vw] max-w-2xl max-h-[88vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send for signing</DialogTitle>
          <DialogDescription>Prepare “{doc?.original_filename}” for secure e-signature. Bundle more documents and drag them into signing order; you'll place signature fields and assign parties next.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. TR1 — 20 Coniston Close" data-testid="signing-title-input" />
          </div>

          <div>
            <Label className="text-xs">Documents in this envelope — drag to reorder</Label>
            <div className="mt-1 border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="signing-bundle-list">
              {items.map((d, i) => (
                <div key={d.id}
                  draggable
                  onDragStart={() => setDragIndex(i)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => onDrop(i)}
                  data-testid={`signing-bundle-item-${d.id}`}
                  className={`flex items-center gap-2 px-2 py-2 bg-white cursor-grab active:cursor-grabbing ${dragIndex === i ? "opacity-50" : ""}`}>
                  <DotsSixVertical weight="bold" className="w-4 h-4 text-slate-400 shrink-0" />
                  <span className="w-5 h-5 flex items-center justify-center rounded bg-slate-900 text-white text-[11px] font-semibold shrink-0">{i + 1}</span>
                  <span className="text-sm text-slate-700 truncate flex-1">{d.original_filename}</span>
                  {items.length > 1 && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button type="button" onClick={() => move(i, -1)} disabled={i === 0}
                        data-testid={`signing-bundle-up-${d.id}`} aria-label="Move up"
                        className="text-slate-400 hover:text-slate-900 disabled:opacity-30">
                        <CaretUp weight="bold" className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1}
                        data-testid={`signing-bundle-down-${d.id}`} aria-label="Move down"
                        className="text-slate-400 hover:text-slate-900 disabled:opacity-30">
                        <CaretDown weight="bold" className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => removeDoc(d.id)} data-testid={`signing-bundle-remove-${d.id}`}
                        className="text-slate-400 hover:text-red-600 ml-1" aria-label="Remove document">
                        <X weight="bold" className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {available.length > 0 && (
            <div>
              <Label className="text-xs">Add more documents (optional)</Label>
              <div className="mt-1 max-h-32 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="signing-extra-docs">
                {available.map((d) => (
                  <button type="button" key={d.id} onClick={() => addDoc(d)}
                    data-testid={`signing-extra-${d.id}`}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50">
                    <Plus weight="bold" className="w-4 h-4 text-slate-500 shrink-0" />
                    <span className="text-sm text-slate-700 truncate">{d.original_filename}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {items.length > 1 && (
            <div className="flex items-start justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
              <div>
                <Label className="text-xs">Also file each document separately</Label>
                <p className="text-[11px] text-slate-500 mt-0.5">On completion, split the signed bundle back into individual PDFs, each stored next to its original.</p>
              </div>
              <Switch checked={splitOutput} onCheckedChange={setSplitOutput} data-testid="signing-split-output-switch" />
            </div>
          )}

          <div>
            <Label className="text-xs">Signing type</Label>
            <Select value={mode} onValueChange={setMode}>
              <SelectTrigger data-testid="signing-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="deed">Deed (TR1, TP1, DS1, charge) — each party nominates a witness + PG82</SelectItem>
                <SelectItem value="standard">Standard (contract) — no witness</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={busy} data-testid="signing-create-confirm">Continue to designer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
